import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Delete, Loader2, LockKeyhole } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import BrandLogo from '@/components/BrandLogo';
import { formatRateLimitMessage } from '@/utils/rateLimitMessage';

const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

export default function AdminPinLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { pinPending, pinRestaurant, completePinLogin, logout, isAuthenticated, isTenantUser, user } = useAuth();
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);

  const fromPath = useMemo(() => location.state?.from?.pathname || '/admin/dashboard', [location.state]);

  useEffect(() => {
    if (isAuthenticated && isTenantUser && user) {
      navigate(fromPath, { replace: true });
      return;
    }
    if (!pinPending) {
      navigate('/admin/login', { replace: true });
    }
  }, [pinPending, isAuthenticated, isTenantUser, user, navigate, fromPath]);

  const submitPin = async (nextPin) => {
    if (nextPin.length !== 4 || loading) return;
    setLoading(true);
    try {
      await completePinLogin(nextPin);
      toast.success('PIN accepted');
      navigate(fromPath, { replace: true });
    } catch (error) {
      setPin('');
      toast.error(formatRateLimitMessage(error, 'Incorrect PIN'));
    } finally {
      setLoading(false);
    }
  };

  const appendDigit = (digit) => {
    if (loading || pin.length >= 4) return;
    const nextPin = `${pin}${digit}`;
    setPin(nextPin);
    if (nextPin.length === 4) {
      submitPin(nextPin);
    }
  };

  const backspace = () => {
    if (loading) return;
    setPin((prev) => prev.slice(0, -1));
  };

  const clear = () => {
    if (loading) return;
    setPin('');
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4 py-10">
      <Card className="w-full max-w-sm border-white/15 bg-white/95 text-slate-950 shadow-2xl shadow-black/50">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto">
            <BrandLogo size="sm" />
          </div>
          <div className="mx-auto inline-flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-700">
            <LockKeyhole className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>Enter Staff PIN</CardTitle>
            <CardDescription>
              {pinRestaurant?.name ? `${pinRestaurant.name}` : 'Restaurant'} access requires a 4-digit PIN.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex items-center justify-center gap-3">
            {[0, 1, 2, 3].map((idx) => (
              <div
                key={idx}
                className={`h-3 w-3 rounded-full border border-slate-300 ${pin.length > idx ? 'bg-slate-900' : 'bg-transparent'}`}
              />
            ))}
          </div>

          <div className="grid grid-cols-3 gap-2">
            {DIGITS.slice(0, 9).map((digit) => (
              <Button
                key={digit}
                type="button"
                variant="outline"
                className="h-12 text-base font-semibold"
                onClick={() => appendDigit(digit)}
                disabled={loading}
              >
                {digit}
              </Button>
            ))}
            <Button type="button" variant="outline" className="h-12" onClick={clear} disabled={loading || pin.length === 0}>
              Clear
            </Button>
            <Button type="button" variant="outline" className="h-12 text-base font-semibold" onClick={() => appendDigit('0')} disabled={loading}>
              0
            </Button>
            <Button type="button" variant="outline" className="h-12" onClick={backspace} disabled={loading || pin.length === 0}>
              <Delete className="h-4 w-4" />
            </Button>
          </div>

          <Button type="button" className="w-full" disabled={loading || pin.length !== 4} onClick={() => submitPin(pin)}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Unlock'}
          </Button>

          <Button type="button" variant="ghost" className="w-full" onClick={logout} disabled={loading}>
            Back to email login
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
